import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import logoSvg from '../../img/thinq_app_logo.svg';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import * as RouterPath from '../../data/RouterPath';
import appQrSvg from '../../img/qrbg.png';
import LanguageIcon from '@mui/icons-material/Language';
import { useLanguageStore } from '../../store/useLanguageStore';
import { useRegionStore } from '../../store/useRegionStore';
import { getExpiryDate, setCookie, getCookie } from '../../utils/cookie';
import useCountryCode from '../../hooks/useCountryCode';
import { getHMacSHA256 } from '../../utils/createSignature';
import { formatDate } from '../../utils/dateUtils';
import { useCountryStore } from '../../store/useCountryStore';

interface ParamsProps {
  id: string;
  password: string;
  country_code: string;
  cross_login: string;
  except_new: string;
  show_login_type: string;
  svc_list: string;
  terms_name: string;
  third_party_service_name: string;
  idpw: boolean;
  language: string;
  response_type: string;
  client_id?: string;
  redirect_uri?: string;
  state: string;
}

function Login() {
  const [id, setId] = useState('');
  const [pw, setPw] = useState('');
  const [empToken, setEmpToken] = useState('');
  const { countryCode: countryCodeStore, setCountryCode: setCountryCodeStore } =
    useCountryStore();
  const [countryCode, setCountryCode] = useState(countryCodeStore || 'KR');
  const [serviceCountryCode, setServiceCountryCode] = useState<
    {
      cntryCode: string;
      cntryName: string;
    }[]
  >([]);

  const [selectedCountryIndex, setSelectedCountryIndex] = useState<number>(0);
  const [koreanIndex, setKoreanIndex] = useState<number>(0);
  const navigate = useNavigate();
  const { setRegion } = useRegionStore();
  const { language, languagePack, setLanguage } = useLanguageStore();
  const { csvData, error } = useCountryCode('/countryCode.csv');

  const createSignature = (apiPath: string, parameter: string) => {
    const date = formatDate(new Date());

    const stringToSign = `${apiPath}?${parameter}`;

    const key = CryptoJS.enc.Utf8.parse(
      process.env.REACT_APP_EMP_CLIENT_SECRET
    );
    const data = CryptoJS.enc.Utf8.parse(stringToSign + `\n${date}`);
    const hashed = CryptoJS.HmacSHA1(data, key);

    return CryptoJS.enc.Base64.stringify(hashed);
  };

  const get_oauth_signature = async (
    path: string,
    params: ParamsProps,
    headers
  ) => {
    try {
      const date = formatDate(new Date());

      const username = encodeURIComponent(params.id);
      const stringToSign = `${path}?country_code=${countryCode}&grant_type=password&password=${params.password}&username=${username}`;

      const key = CryptoJS.enc.Utf8.parse(
        process.env.REACT_APP_EMP_CLIENT_SECRET
      );
      const data = CryptoJS.enc.Utf8.parse(stringToSign + `\n${date}`);
      const hashed = CryptoJS.HmacSHA1(data, key);
      const createdSignature = CryptoJS.enc.Base64.stringify(hashed);

      const token = getTokenByPassword(
        createdSignature,
        date,
        stringToSign,
        username,
        params.password,
        headers
      );

      if (token === null) return null;

      return token;
    } catch (err) {
      console.log(err);
    }
  };

  const getTokenByPassword = async (signature, date, path, id, pw, headers) => {
    const params = new URLSearchParams({
      country_code: countryCode,
      grant_type: 'password',
      password: pw,
      username: id,
    });
    headers = {
      ...headers,
      'x-lge-oauth-signature': signature,
      'x-lge-oauth-date': date,
    };

    try {
      const response = await axios.post(
        `https://${countryCode}.${process.env.REACT_APP_EMP_AUTH_URI}${path}`,
        params,
        { headers }
      );
      return response;
    } catch (err) {
      console.log(err);
      alert('Login failed.');
      return null;
    }
  };

  const handleOnKeyPress = (event) => {
    if (event.key === 'Enter') {
      onSubmit(); // Enter 입력이 되면 클릭 이벤트 실행
    }
  };

  const handleCountryCodeChange = (value) => {
    setCountryCode(serviceCountryCode[value].cntryCode);
    setSelectedCountryIndex(Number(value));
    setLanguage(Number(value) === koreanIndex ? 'ko' : 'en');
    setCountryCodeStore(serviceCountryCode[value].cntryCode);
    setRegion(csvData[countryCode]);
  };

  const onSubmit = () => {
    const path = '/oauth/1.0/oauth2/token';
    const passwordInSha512 = CryptoJS.SHA512(pw).toString(CryptoJS.enc.Hex);
    const params: ParamsProps = {
      id: id,
      password: passwordInSha512,
      country_code: countryCode,
      cross_login: '',
      except_new: '',
      show_login_type: '',
      svc_list: '',
      terms_name: '',
      third_party_service_name: '',
      idpw: true,
      language: language === 'ko' ? 'ko-KR' : 'en-US',
      response_type: 'code',
      client_id: process.env.REACT_APP_CLOUDEMP_CLIENT_ID,
      redirect_uri: process.env.REACT_APP_THINQ_REDIRECT_URI,
      state: 'aa123',
    };

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      'x-lge-appkey': process.env.REACT_APP_EMP_CLIENT_KEY,
    };

    try {
      get_oauth_signature(path, params, headers).then(async (res: any) => {
        try {
          const tokenSignature = createSignature(
            `/oauth/1.5/token`,
            `access_token=${res.data.access_token}`
          );

          const date = formatDate(new Date());

          const profileHeader = {
            'Content-Type': 'application/x-www-form-urlencoded',
            'x-lge-oauth-signature': tokenSignature,
            'x-lge-appkey': process.env.REACT_APP_EMP_CLIENT_KEY,
            'x-lge-oauth-date': date,
            Accept: 'application/json',
          };
          await axios
            .get(
              `https://${countryCode}.${process.env.REACT_APP_EMP_AUTH_URI}/oauth/1.5/token?access_token=${res.data.access_token}`,
              {
                headers: profileHeader,
              }
            )
            .then((res) => {
              setCountryCode(res.data.country_code);
              console.log(res.data.country_code, csvData[countryCode]);
            });
          setCookie('bearer_token', res.data.access_token, {
            path: '/',
            secure: '/',
            expires: getExpiryDate(2),
          });
          navigate(RouterPath.TOKENS, {
            state: {
              type: 'login',
              emp: res.data,
            },
          });
        } catch (e) {
          console.log(e);
        }
      });
    } catch (e) {
      console.log(e);
      alert('Login failed.');
    }
  };

  useEffect(() => {
    setLanguage('ko');
    const fetchData = async () => {
      const timestamp = Math.floor(Date.now() / 1000);
      const xSignature = getHMacSHA256(
        String(timestamp),
        process.env.REACT_APP_EMP_APP_KEY as string
      );

      try {
        const response = await axios.get(
          process.env.REACT_APP_EMP_COUNTRY_URL as string,
          {
            headers: {
              'X-Application-Key': process.env.REACT_APP_EMP_APP_KEY,
              'X-Signature': xSignature,
              'X-Timestamp': timestamp,
              'X-Device-Country': 'KR',
              'X-Device-Publish-Flag': 'Y', // Y 운영, N 테스트
              'X-Device-Type': 'P01',
              'X-Device-Platform': 'PC',
              'X-Device-Language': 'ko-KR',
              'X-Device-Language-Type': 'IETF',
              'Content-Type': 'application/json;charset=UTF-8',
            },
          }
        );
        const cntryList = response.data.info.serviceList[0].cntryList;
        setServiceCountryCode(cntryList);

        const krIndex = cntryList.findIndex(
          (country) => country.cntryCode === 'KR'
        );
        setKoreanIndex(krIndex);
        if (krIndex !== -1) {
          setSelectedCountryIndex(krIndex);
          setCountryCode('KR');
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchData();
  }, []);
  useEffect(() => {
    setCountryCodeStore(countryCode);
    setRegion(csvData[countryCode]);
  }, [countryCode, countryCodeStore]);

  return empToken ? (
    <>loading...</>
  ) : (
    <>
      <div className="select-wrapper">
        <div className="language">
          <TextField
            select
            value={selectedCountryIndex}
            size="small"
            sx={{ width: '260px' }}
            onChange={(e) => {
              handleCountryCodeChange(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LanguageIcon />
                </InputAdornment>
              ),
            }}
          >
            {serviceCountryCode &&
              serviceCountryCode.map((item, index) => (
                <MenuItem
                  value={String(index)}
                  key={`${index}-${item.cntryName}`}
                >
                  {item.cntryName}
                </MenuItem>
              ))}
          </TextField>
        </div>
      </div>
      <Box className="main home" component="main">
        <Box className="main-body">
          <Box className="welcome">
            <Typography variant="h1" sx={{ flexGrow: 1 }}>
              <img src={logoSvg} alt="LG ThinQ App Logo" />
              <strong>
                LG <span className="red">ThinQ</span>
              </strong>
            </Typography>
            <Typography variant="body1" className="welcome-message">
              <strong> {languagePack && languagePack.login.gritting}</strong>
              <small> {languagePack && languagePack.login.title}</small>
            </Typography>
            <Stack className="lg-login" spacing={1}>
              <TextField
                id="standard-basic-id"
                variant="outlined"
                placeholder={
                  languagePack && languagePack.login.loginInputPlaceholder
                }
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">ID</InputAdornment>
                  ),
                }}
                onChange={(event) => {
                  setId(event.target.value);
                }}
              />
              <TextField
                id="standard-basic-pw"
                variant="outlined"
                type="password"
                placeholder={
                  languagePack &&
                  languagePack.login.loginInputPasswordPlaceholder
                }
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">PW</InputAdornment>
                  ),
                }}
                onChange={(event) => {
                  setPw(event.target.value);
                }}
                onKeyPress={(event) => {
                  handleOnKeyPress(event);
                }}
              />
              <Button variant="contained" size="large" onClick={onSubmit}>
                {selectedCountryIndex == 77 ? '로그인' : 'LOGIN'}
              </Button>
            </Stack>
            <Link
              className="btn-login"
              onClick={() => {
                navigate(RouterPath.APP_LOGIN);
              }}
            >
              <span> {languagePack && languagePack.login.otpOrQR}</span>
              <strong> {languagePack && languagePack.login.login}</strong>
            </Link>
          </Box>
        </Box>
        <Box className="main-footer">
          <img src={appQrSvg} alt="LG ThinQ App QR Code Sample" />
          <Typography variant="body2">
            <strong> {languagePack && languagePack.login.isThinqFirst}</strong>
            <span>{languagePack && languagePack.login.register}</span>
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default Login;
